import { DatePicker, Select, Table, TableColumnsType } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import QueryString from 'qs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { getInquiries } from '../../../api/boardCommunication.api';
import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Pagination';
import SelectCampus from '../../../components/SelectCampus';
import useAxios from '../../../hooks/useAxios';
import config from '../../../lib/config';
import { getCommonCodeLabel } from '../../../lib/store';
import {
  IGetInquiries,
  IInquiryItem,
} from '../../../models/boardCommunication.model';
import { RootState } from '../../../store/rootReducer';

const BoardInquiry = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { auth, currentCampus } = useSelector((state: RootState) => state.auth);
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // BR0001 = 소통창구 구분
  const BR0001 = commonCodes.find(
    (item) => item.commonCode === 'BR0001',
  )?.detailCode;
  // BR0002 = 소통창구 상태
  const BR0002 = commonCodes.find(
    (item) => item.commonCode === 'BR0002',
  )?.detailCode;
  const { data: res, isLoading, error, execute } = useAxios<IGetInquiries>();

  const [communicationDiv, setCommunicationDiv] = useState<string>();
  const [communicationStatus, setCommunicationStatus] = useState<string>();
  const [srtDate, setSrtDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [title, setTitle] = useState('');
  const [page, setPage] = useState(1);
  const [param, setParam] = useState({});
  const [data, setData] = useState<IInquiryItem[]>([]);

  useEffect(() => {
    execute(getInquiries());
  }, []);

  useEffect(() => {
    if (res && !isLoading && currentCampus) {
      const _param: any = {};
      let _data: IInquiryItem[] = _.cloneDeep(res.data);
      _data = _data.filter(item => item.campusCode === currentCampus);

      if (query.page) {
        _param.page = Number(query.page);
        setPage(_param.page);
      }
      if (query.communicationDiv) {
        _param.communicationDiv = String(query.communicationDiv);
        setCommunicationDiv(_param.communicationDiv);
        _data = _data.filter(item => item.communicationDiv?.trim() === _param.communicationDiv);
      }
      if (query.communicationStatus) {
        _param.communicationStatus = String(query.communicationStatus);
        setCommunicationStatus(_param.communicationStatus);
        _data = _data.filter(item => item.communicationStatus === _param.communicationStatus);
      }
      if (query.srtDate && query.endDate) {
        _param.srtDate = String(query.srtDate);
        _param.endDate = String(query.endDate);
        setSrtDate(_param.srtDate);
        setEndDate(_param.endDate);
        _data = _data.filter(
          item =>
            moment(item.regDate).isAfter(_param.srtDate) &&
            moment(item.regDate).isBefore(moment(_param.endDate).add(1, 'days')),
        );
      }
      if (query.title) {
        _param.title = String(query.title);
        setTitle(_param.title);
        _data = _data.filter(item => item.title.includes(_param.title));
      }

      setParam(_param);
      setData(_data);
    }
  }, [res, isLoading, location, currentCampus]);

  const onSearch = () => {
    const param: any = { page: 1 };
    param.campusCode = currentCampus;
    if (communicationDiv) param.communicationDiv = communicationDiv;
    if (communicationStatus) param.communicationStatus = communicationStatus;
    if (srtDate && endDate) {
      param.srtDate = moment(srtDate).format('YYYYMMDD');
      param.endDate = moment(endDate).format('YYYYMMDD');
    }
    if (title) param.title = title;

    history.push(`/board/inquiry?${QueryString.stringify(param)}`);
  };

  const columns: TableColumnsType<IInquiryItem> = [
    {
      title: t('common.column.num2'),
      dataIndex: 'communicationCode',
      key: 'communicationCode',
      width: '7%',
      sorter: (a, b) => a.communicationCode - b.communicationCode,
    },
    {
      title: t('board.inquiry.column1'),
      dataIndex: 'communicationDiv',
      key: 'communicationDiv',
      width: '10%',
      sorter: (a, b) => a.communicationDiv?.localeCompare(b.communicationDiv),
      render: (value) => getCommonCodeLabel('BR0001', value?.trim(), 'ko'),
    },
    {
      title: t('common.column.title'),
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      sorter: (a, b) => a.title?.localeCompare(b.title),
      render: value => {
        return <div className="text-left ant-table-cell-ellipsis">{value}</div>
      }
    },
    {
      title: t('common.menu.campus'),
      dataIndex: 'campusCode',
      key: 'campusCode',
      width: '10%',
      sorter: (a, b) => a.campusCode?.localeCompare(b.campusCode),
      render: (value: string) => {
        const campus = campusData?.find((item) => item.campusCode === value);
        return campus ? campus.campusKorName : value;
      },
    },
    {
      title: t('archive.learn.column1'),
      dataIndex: 'regMemKorName',
      key: 'regMemKorName',
      width: '10%',
      sorter: (a, b) => a.regMemKorName?.localeCompare(b.regMemKorName),
    },
    {
      title: t('archive.learn.column2'),
      dataIndex: 'regDate',
      key: 'regDate',
      width: '10%',
      sorter: (a, b) => a.regDate?.localeCompare(b.regDate),
      render: (value: string) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('board.inquiry.column2'),
      dataIndex: 'communicationStatus',
      key: 'communicationStatus',
      width: '10%',
      sorter: (a, b) => a.answerDate?.localeCompare(b.answerDate),
      render: (value, record) => {
        const status = getCommonCodeLabel('BR0002', value, i18n.language);
        const date = record.answerDate
          ? `(${moment(record.answerDate).format('YYMMDD')})`
          : null;
        return date ? (
          <>
            {status} {date}
          </>
        ) : (
          status
        );
      },
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-board-big.svg"
      parentMenu={t('common.menu.board')}
      currentMenu={t('board.inquiry.title1')}
      onlyTitle={true}
    />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label>
            <div>
              <SelectCampus />
            </div>
          </li>
          <li>
            <label>{t('board.inquiry.column1')}</label>
            <div>
              <Select
                placeholder={t('board.inquiry.column1')}
                value={communicationDiv}
                onChange={(value) => setCommunicationDiv(value)}
              >
                <Select.Option value="">
                  {t('common.text.all')}
                </Select.Option>
                {BR0001?.map((item) => (
                  <Select.Option
                    key={item.detailCode}
                    value={item.detailCode}
                  >
                    {i18n.language === 'ko' ? item.korName : item.engName}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </li>
          <li>
            <label>{t('board.inquiry.column2')}</label>
            <div>
              <Select
                placeholder={t('board.inquiry.column2')}
                value={communicationStatus}
                onChange={(value) => setCommunicationStatus(value)}
              >
                <Select.Option value="">
                  {t('common.text.all')}
                </Select.Option>
                {BR0002?.map((item: any) => (
                  <Select.Option
                    key={item.detailCode}
                    value={item.detailCode}
                  >
                    {i18n.language === 'ko' ? item.korName : item.engName}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </li>
          <li>
            <label>{t('archive.learn.column2')}</label>
            <div className="date-picker__area">
              <DatePicker
                value={srtDate ? moment(srtDate) : undefined}
                onChange={(e) => {
                  if (e === null) setSrtDate('');
                  else setSrtDate(moment(e).format('YYYYMMDD'));
                }}
              />
              ~
              <DatePicker
                value={endDate ? moment(endDate) : undefined}
                onChange={(e) => {
                  if (e === null) setEndDate('');
                  else setEndDate(moment(e).format('YYYYMMDD'));
                }}
              />
            </div>
          </li>
          <li>
            <label>{t('common.column.title')}</label>
            <input
              type="text"
              className="form-control w-150p"
              placeholder={t('board.inquiry.placeholder1')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') onSearch();
              }}
            />
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />

      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">
            {t('common.text.count')}: <b>{data?.length || 0}</b>{t('common.text.unit')}
          </div>          
          {auth?.hqYn === 'N' ? (
            <div className="comm-btn__area">
              <Button
                className="btn-basic pink"
                onClick={() => history.push('/board/inquiry/create')}
                text={t('common.button.create')}
              />
            </div>
          ) : null}
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  history.push(
                    `/board/inquiry/${record.communicationCode}${location.search}`,
                  );
                },
              };
            }}
            rowClassName={(record) =>
              'pointer' +
              (record.communicationStatus === 'FI' ? ' chosen' : '')
            }
            rowKey="communicationCode"
          />
        </div>
        <div className="comm-bx__between">
          <div className="comm-btn__area" />
          <div className="common-paging center">
            <Pagination
              count={data?.length || 0}
              page={page}
              param={param}
              path="/board/inquiry"
            />
          </div>
          {auth?.hqYn === 'Y' ? (
            <div className="comm-btn__area right">
              <Button
                className="btn-basic pink"
                onClick={() => history.push('/board/inquiry/create')}
                text={t('common.button.create')}
              />
            </div>
          ) : null}
        </div>
        <div className="row">
          <div className="col-3 text-right">

          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default BoardInquiry;
